import {inject, Service} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {take} from 'rxjs/operators';

@Service()
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly queue: QueuedNotification[] = [];
  private isDisplaying = false;

  success(summary: string, detail: string, duration = 5000): void {
    this.enqueue('success', summary, detail, duration);
  }

  warning(summary: string, detail: string, duration = 10000): void {
    this.enqueue('warning', summary, detail, duration);
  }

  error(summary: string, detail: string, duration = 10000): void {
    this.enqueue('error', summary, detail, duration);
  }

  private enqueue(severity: NotificationSeverity, summary: string, detail: string, duration: number): void {
    this.queue.push({severity, summary, detail, duration});
    this.displayNext();
  }

  private displayNext(): void {
    if (this.isDisplaying || this.queue.length === 0) {
      return;
    }

    this.isDisplaying = true;
    const notification = this.queue.shift()!;
    const ref = this.snackBar.open(
      `${notification.summary}: ${notification.detail}`,
      'Dismiss',
      {
        duration: notification.duration,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        politeness: notification.severity === 'error' ? 'assertive' : 'polite',
        panelClass: [
          'app-notification',
          `app-notification-${notification.severity}`
        ]
      }
    );

    ref.afterDismissed().pipe(take(1)).subscribe(() => {
      this.isDisplaying = false;
      this.displayNext();
    });
  }
}

type NotificationSeverity = 'success' | 'warning' | 'error';

interface QueuedNotification {
  severity: NotificationSeverity;
  summary: string;
  detail: string;
  duration: number;
}
